import { Injectable, NgZone, OnDestroy } from '@angular/core';
import webstomp from 'webstomp-client/dist/webstomp';
import SockJS from 'sockjs-client';
import { Observer } from '../models/observer.interface';
import { Observable } from '../models/observable.interface';
import { AuthCoreService } from './auth-core.service';
import {DeterminationModeApplicationService} from './determination-mode-application.service';
import {NotificationServiceJsVersStatutWebsocketService} from '../components/statut-websocket/notification-service-js-vers-statut-websocket.service';
import {StatutWebsocketDto} from '../components/statut-websocket/statut-websocket-dto.model';
import {Subscription} from 'rxjs';

class Topic implements Observable {
	observers: Observer[] = [];
	subscription: any;

	constructor(public url: string, public topicName: string) {
	}

	public subscribe(observer: Observer | Observer[]) {
		if (Array.isArray(observer)) {
			for (const obs of observer) {
				if (this.observers.indexOf(obs) === -1) {
					this.observers.push(obs);
				}
			}
		}
		else {
			if (this.observers.indexOf(observer) === -1) {
				this.observers.push(observer);
			}
		}
	}

	public unsubscribe(observer: Observer | Observer[]) {
		if (Array.isArray(observer)) {
			for (const obs of observer) {
				const index = this.observers.indexOf(obs, 0);
				if (index > -1) {
					this.observers.splice(index, 1);
				}
			}
		}
		else {
			const index = this.observers.indexOf(observer, 0);
			if (index > -1) {
				this.observers.splice(index, 1);
			}
		}
	}

	public notify(msg: any): void {
		for (const observer of this.observers) {
			observer.notify(msg.body);
		}
	}
}

@Injectable()
export class NotifierService implements OnDestroy {

	private notificationServletRoot = '/websocket';

	/**
	 * Endpoint STOMP.
	 * cf. WebSocketConfiguration.ENDPOINT
	 * @type {string}
	 */
	private endPoint = '/endpoint';

	/**
	 * Nom du broker
	 * cf. WebSocketConfiguration.SIMPLE_BROKER_NAME
	 * @type {string}
	 */
	private channelTopic = '/topic';

	/**
	 * Topics auxquels on est connecté, indexés par nom de topic.
	 * @type {{}}
	 */
	private topics: Topic[] = [];

	connected = false;

	private socket;
	private pendingSubscriptions: Topic[] = [];

	private statutWebsocket: StatutWebsocketDto;
	private notificationStatutWebsocketSubjectSubscription: Subscription;
	private notificationDemandeConnexionWebsocketSubjectSubscription: Subscription;

	protected urlBackend: string = '';

	constructor(protected authService: AuthCoreService, protected ngZone: NgZone,
				protected notificationServiceJsVersStatutWebsocketService: NotificationServiceJsVersStatutWebsocketService,
				protected determinationModeApplicationService: DeterminationModeApplicationService) {
		// Dans le cas d'une application en cohabitation, on ne doit pas utiliser la connexion websocket en angular
		if (!this.determinationModeApplicationService.estModeApplicationCohabitationAngularJsAngular()) {
			this.notificationStatutWebsocketSubjectSubscription = this.notificationServiceJsVersStatutWebsocketService.notificationStatutWebsocketSubject.subscribe((statutWebsocket: StatutWebsocketDto) => {
				if (!this.statutWebsocket || this.statutWebsocket.estConnectee || statutWebsocket.estConnectee) {
					this.statutWebsocket = statutWebsocket;
				}
			});
			this.notificationDemandeConnexionWebsocketSubjectSubscription = this.notificationServiceJsVersStatutWebsocketService.notificationDemandeConnexionWebsocketSubject.subscribe(() => this.connectToWebsocketAngular());
		}
	}

	ngOnDestroy(): void {
		this.disconnectFromAll();
		this.notificationDemandeConnexionWebsocketSubjectSubscription.unsubscribe();
		this.notificationStatutWebsocketSubjectSubscription.unsubscribe();
	}

	public disconnectFromAll() {
		for (const topicName in this.topics) {
			this.disconnect([this.topics[topicName].topicName]);
		}
	}

	public disconnectFromTopic(topicName: string) {
		this.ngZone.runOutsideAngular(() => {
			this.disconnect([topicName]);
		});
	}

	/**
	 * Créé la connexion à la websocket.
	 */
	public connectToWebsocketAngular() {
		this.ngZone.runOutsideAngular(() => {
			const self = this;

			function onSuccess() {
				self.connected = true;
				for (const topic of self.pendingSubscriptions) {
					self.doSubscription(topic);
				}
				self.pendingSubscriptions = [];

				self.notificationServiceJsVersStatutWebsocketService.posterStatutWebsocket(true, true);
			}

			function onError() {
				self.connected = false;
				self.notificationServiceJsVersStatutWebsocketService.posterStatutWebsocket(false, false);
			}

			// la websocket s'est déconnecté, on retente une connexion après s'être désabonné à tous les topics
			this.pendingSubscriptions = [];
			for (const topicName in this.topics) {
				const topic = this.topics[topicName];
				this.pendingSubscriptions.push(topic);
				if (topic.subscription) {
					topic.subscription.unsubscribe();
				}
			}
			this.connected = false;
			this.socket = this.createSocket();
			const headers = {
				contentType: 'application/json',
				logLevel: 'off',
				// Use local timer to reconnect
				maxReconnectOnClose: 0,
				Authorization: this.authService.getToken()
			};
			this.socket.connect(headers, onSuccess, onError);
		});
	}

	/**
	 * S'inscrit au topic demandé.
	 * @param {Observer} observer l'observer qui sera notifié à la réception de messages.
	 * @param {String} topicName Le nom du topic auquel on veut s'inscrire.
	 */
	public subscribeToTopic(observer: Observer | Observer[], topicName) {
		this.ngZone.runOutsideAngular(() => {
			const topic = this.createTopic(observer, topicName);

			if (!this.connected) {
				// si le websocket n'est pas encore connecté, on enregistre la demande de souscription pour la réappliquer juste après la connexion
				this.pendingSubscriptions.push(topic);
				return;
			}

			this.doSubscription(topic);
		});
	}

	private doSubscription(topic: Topic) {
		// le websocket est connecté, on s'inscrit au topic désiré
		topic.subscription = this.socket.subscribe(topic.url, topic.notify.bind(topic));
	}

	private createSocket(): any {
		const basePathApi = (this.urlBackend ?? '') + (this.determinationModeApplicationService.estModeApplicationCohabitationAngularJsAngular() ? '/ws' : '');
		const sockJS = SockJS(basePathApi + this.notificationServletRoot + this.endPoint, null);
		return webstomp.over(sockJS, {debug: false});
	}

	private createTopic(observer: Observer | Observer[], topicName): any {
		const topic = new Topic(this.channelTopic + '/' + topicName, topicName);
		topic.subscribe(observer);
		this.topics[topicName] = topic;
		return topic;
	}

	/**
	 * Se déconnecte de tous les topics demandés.
	 * @param topicNames les topics pour lesquels on veut se déconencter.
	 */
	private disconnect(topicNames: any[]): void {
		for (const topicName of topicNames) {
			if (this.topics[topicName] && this.topics[topicName].subscription) {
				this.topics[topicName].subscription.unsubscribe();
				delete this.topics[topicName];
			}
		}
	}
}
